/**
 * CarShadowTexture — Procedural soft oval shadow for ground projection.
 *
 * Generates a greyscale shadow texture entirely in the browser — no static
 * file needed. Follows the same "return-canvas" pattern as CarbonTexture so
 * it can be passed directly to THREE.CanvasTexture:
 *
 *   const map = new THREE.CanvasTexture(new CarShadowTexture(512) as unknown as HTMLCanvasElement);
 *
 * Usage as alphaMap (recommended):
 *   material.alphaMap = new THREE.CanvasTexture(new CarShadowTexture() as unknown as HTMLCanvasElement)
 *   material.color.set('#000000')
 *   material.transparent = true
 *   material.depthWrite = false
 *
 * Pixel design:
 * - Elliptical soft-body: horizontal radius 46%, vertical radius 38% of canvas
 *   (typical car outline is wider than it is deep when viewed from top).
 * - Falloff: smoothstep² → natural "pooling" shadow, darkest at centre.
 * - White = fully opaque (shadow), black = fully transparent (outside).
 *
 * @see docs/ims-engine-api/11-feature-lighting-shadow.md
 */

export class CarShadowTexture {
  readonly width: number;
  readonly height: number;

  constructor(size = 512) {
    this.width  = size;
    this.height = size;

    const canvas = document.createElement('canvas');
    canvas.width  = size;
    canvas.height = size;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      const imageData = ctx.createImageData(size, size);
      const data = imageData.data;

      const cx = size / 2;
      const cy = size / 2;

      // Ellipse radii in normalised (0–1) canvas-space.
      // rx > ry because a car is wider than it is deep from above.
      const rx = 0.46;  // horizontal
      const ry = 0.38;  // vertical (front-to-back depth is slightly less)

      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          // Normalised distance from centre, ellipse-adjusted
          const nx = (x - cx) / (size * rx);
          const ny = (y - cy) / (size * ry);
          const d  = Math.sqrt(nx * nx + ny * ny); // 0 = centre, 1 = ellipse edge

          // Smooth falloff: 1 at centre → 0 at edge
          // smoothstep² gives a natural "shadow pool" effect
          const raw  = Math.max(0, 1 - d);
          const ss   = raw * raw * (3 - 2 * raw); // smoothstep
          const alpha = ss * ss;                  // squared for sharper center, soft rim

          const idx     = (y * size + x) * 4;
          data[idx]     = 255;              // R (white → used as alpha source)
          data[idx + 1] = 255;              // G
          data[idx + 2] = 255;              // B
          data[idx + 3] = Math.round(alpha * 255); // A
        }
      }

      ctx.putImageData(imageData, 0, 0);
    }

    // Return the canvas so `new THREE.CanvasTexture(new CarShadowTexture())` works
    // — identical trick to FlakesTexture / CarbonTexture.
    return canvas as unknown as this;
  }
}
